import React, { useEffect } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useForm } from 'react-hook-form';

export interface DocumentFormData {
  documentName: string;
  iManageDocNumber: string;
}

export interface DocumentFormProps {
  data: DocumentFormData | null;
  mode: 'add' | 'edit' | 'view';
  onCancel: () => void;
  onSave?: (data: DocumentFormData) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  showDelete?: boolean;
}

const DEFAULT_VALUES: DocumentFormData = {
  documentName: '',
  iManageDocNumber: '',
};

/**
 * Document form component for viewing/editing engagement letter documents.
 * Uses react-hook-form for form state management and validation.
 */
export function DocumentForm({ data, mode, onCancel, onSave, onDelete, showDelete }: Readonly<DocumentFormProps>) {
  const isEditMode = mode === 'edit' || mode === 'add';

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DocumentFormData>({
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    reset({
      documentName: data?.documentName ?? '',
      iManageDocNumber: data?.iManageDocNumber ?? '',
    });
  }, [data, reset]);

  const getTitleText = () => {
    if (mode === 'add') return 'Add Document';
    if (mode === 'edit') return 'Edit Document';
    return 'View Document';
  };

  const getDescriptionText = () => {
    if (mode === 'add') return 'Enter the details for the new engagement letter document.';
    if (mode === 'edit') return 'Update the selected engagement letter document details.';
    return 'Review the selected engagement letter document.';
  };

  const onSubmit = async (formData: DocumentFormData) => {
    await onSave?.(formData);
  };

  return (
    <Stack spacing={2} component="form" onSubmit={(e: React.BaseSyntheticEvent) => void handleSubmit(onSubmit)(e)}>
      <Box>
        <Typography id="user-modal-title" variant="h6">
          {getTitleText()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {getDescriptionText()}
        </Typography>
      </Box>

      <TextField
        label="Document Name"
        {...register('documentName', { required: isEditMode && 'Document name is required' })}
        error={Boolean(errors.documentName)}
        helperText={errors.documentName?.message}
        fullWidth
        slotProps={{ htmlInput: { readOnly: !isEditMode } }}
      />
      <TextField
        label="iManage Doc Number"
        {...register('iManageDocNumber', { required: isEditMode && 'Doc number is required' })}
        error={Boolean(errors.iManageDocNumber)}
        helperText={errors.iManageDocNumber?.message}
        fullWidth
        slotProps={{ htmlInput: { readOnly: !isEditMode } }}
      />

      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
        {showDelete ? (
          <Button variant="outlined" color="error" onClick={() => void onDelete?.()}>
            Delete
          </Button>
        ) : null}
        <Button variant="outlined" onClick={onCancel}>
          {isEditMode ? 'Cancel' : 'Close'}
        </Button>
        {isEditMode ? (
          <Button variant="contained" type="submit">
            Save
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}

export default DocumentForm;
